import styled from "@emotion/styled";
import { useEffect, useMemo, useRef, useState } from "react";
import { AnalysisEventPoint, AnalysisRegion, AnalysisRegions } from "../../api/analysis";
import HeatLayer from "../heatmap/HeatLayer";
import {
  CHORO_COLORS,
  EVENT_GROUPS,
  EVENT_TYPES,
  EventTypeMeta,
  RESULT_LABEL,
  classOf,
  fmt1,
  fmtInt,
  per10kHours,
  quantileBreaks,
} from "./analysisUtil";

/**
 * 빅데이터 분석 지도 (B1 사고·위험운전 핫스팟 / 밀도, B2 행정동 단계구분도)
 *
 * 카카오 API 를 직접 쓴다. 클러스터·다각형 클릭처럼 통합 지도 컴포넌트에 없는 기능이 필요해서다.
 * 밀도는 히트맵 화면의 HeatLayer 를 그대로 쓴다.
 */

export type MapMode = "hotspot" | "density" | "region";
export type RegionMetric = "trips" | "hours" | "rollover" | "danger" | "rate";
type Group = EventTypeMeta["group"];

/** 운행시간이 이보다 적은 행정동은 비율이 튀므로 사고율 단계에서 뺀다 */
export const RATE_MIN_HOURS = 100;

export const REGION_METRICS: { key: RegionMetric; label: string; unit: string }[] = [
  { key: "rate", label: "1만 운행시간당 전복", unit: "건" },
  { key: "rollover", label: "전복 건수", unit: "건" },
  { key: "danger", label: "위험운전 건수", unit: "건" },
  { key: "trips", label: "운행 건수", unit: "건" },
  { key: "hours", label: "운행시간", unit: "시간" },
];

const metricValue = (r: AnalysisRegion, m: RegionMetric) => {
  if (m === "rate") return r.hours >= RATE_MIN_HOURS ? per10kHours(r.rollover, r.hours) : 0;
  return r[m];
};

interface Props {
  events: AnalysisEventPoint[];
  regions?: AnalysisRegions;
  mode: MapMode;
  groups: Set<Group>;
  metric: RegionMetric;
  height: number;
  loading?: string | null;
  /** 차종 코드 → 이름 */
  typeName?: (code: number | null) => string;
  /** 팝업의 '원시 데이터 보기' */
  onOpenRaw?: (id: number) => void;
}

const getKakao = (): any => (window as any).kakao;

function dotImage(kakao: any, color: string) {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14">' +
    `<circle cx="7" cy="7" r="5" fill="${color}" stroke="#fff" stroke-width="2"/></svg>`;
  return new kakao.maps.MarkerImage(
    "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    new kakao.maps.Size(14, 14),
    { offset: new kakao.maps.Point(7, 7) }
  );
}

const esc = (s: unknown) =>
  String(s ?? "").replace(
    /[&<>"]/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string)
  );

export default function AnalysisMap({ events, regions, mode, groups, metric, height, loading, typeName, onOpenRaw }: Props) {
  const elRef = useRef<HTMLDivElement | null>(null);
  // 마커를 다시 만들지 않도록 콜백은 ref 로 들고 있는다
  const typeNameRef = useRef(typeName);
  typeNameRef.current = typeName;
  const onOpenRawRef = useRef(onOpenRaw);
  onOpenRawRef.current = onOpenRaw;
  const [map, setMap] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const clustererRef = useRef<any>(null);
  const infowRef = useRef<any>(null);
  const polysRef = useRef<any[]>([]);
  const [picked, setPicked] = useState<AnalysisRegion | null>(null);

  // ── 지도 만들기 ──
  useEffect(() => {
    const kakao = getKakao();
    if (!elRef.current || map) return;
    if (!kakao?.maps) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => setFailed((f) => f || !cancelled), 5000);
    kakao.maps.load(() => {
      if (cancelled || !elRef.current) return;
      window.clearTimeout(timer);
      const m = new kakao.maps.Map(elRef.current, {
        center: new kakao.maps.LatLng(36.3, 127.9),
        level: 12,
      });
      m.addControl(new kakao.maps.ZoomControl(), kakao.maps.ControlPosition.RIGHT);
      infowRef.current = new kakao.maps.InfoWindow({ removable: true, zIndex: 20 });
      if (kakao.maps.MarkerClusterer) {
        clustererRef.current = new kakao.maps.MarkerClusterer({
          map: m,
          averageCenter: true,
          minLevel: 8,
          gridSize: 50,
          disableClickZoom: false,
        });
      }
      setMap(m);
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [map]);

  // 컨테이너 크기가 바뀌면 다시 계산한다
  useEffect(() => {
    const el = elRef.current;
    if (!map || !el || typeof ResizeObserver === "undefined") return;
    let last = "";
    const ro = new ResizeObserver(() => {
      const key = el.clientWidth + "x" + el.clientHeight;
      if (key === last || !el.clientWidth || !el.clientHeight) return;
      last = key;
      const c = map.getCenter();
      map.relayout();
      map.setCenter(c);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);

  const shownEvents = useMemo(
    () => events.filter((e) => EVENT_TYPES[e.type] && groups.has(EVENT_TYPES[e.type].group)),
    [events, groups]
  );

  // ── 처음 데이터가 오면 범위 맞추기 ──
  const fitKeyRef = useRef("");
  useEffect(() => {
    const kakao = getKakao();
    if (!map || !kakao) return;
    const pts: [number, number][] =
      mode === "region" && regions
        ? Object.values(regions.polygons).flat()
        : shownEvents.map((e) => [e.lat, e.lot] as [number, number]);
    if (!pts.length) return;
    const key = `${mode === "region" ? "r" : "e"}:${pts.length}:${pts[0]?.join(",")}`;
    if (key === fitKeyRef.current) return;
    fitKeyRef.current = key;
    // 울릉도·제주처럼 멀리 떨어진 몇 곳 때문에 전체가 작아지지 않게 양 끝 2% 를 잘라 범위를 잡는다
    const q = (arr: number[], p: number) => arr[Math.min(arr.length - 1, Math.max(0, Math.floor(arr.length * p)))];
    const lats = pts.map((x) => x[0]).sort((a, c) => a - c);
    const lngs = pts.map((x) => x[1]).sort((a, c) => a - c);
    const trim = pts.length >= 50 ? 0.02 : 0;
    const b = new kakao.maps.LatLngBounds(
      new kakao.maps.LatLng(q(lats, trim), q(lngs, trim)),
      new kakao.maps.LatLng(q(lats, 1 - trim), q(lngs, 1 - trim))
    );
    map.relayout();
    map.setBounds(b);
  }, [map, mode, regions, shownEvents]);

  // ── 핫스팟 마커 ──
  useEffect(() => {
    const kakao = getKakao();
    const cl = clustererRef.current;
    if (!map || !kakao) return;
    cl?.clear();
    infowRef.current?.close();
    if (mode !== "hotspot") return;
    const icons: Record<string, any> = {};
    const markers = shownEvents.map((e) => {
      const meta = EVENT_TYPES[e.type];
      icons[e.type] = icons[e.type] ?? dotImage(kakao, meta.color);
      const marker = new kakao.maps.Marker({
        position: new kakao.maps.LatLng(e.lat, e.lot),
        image: icons[e.type],
        title: meta.label,
      });
      kakao.maps.event.addListener(marker, "click", () => {
        const tn = typeNameRef.current?.(e.type_cd) ?? "";
        const box = document.createElement("div");
        box.style.cssText = "padding:8px 10px;font-size:12px;line-height:1.55;min-width:200px;max-width:280px";
        box.innerHTML = `<b style="color:${meta.color}">${esc(meta.label)}</b><br>
             ${esc(e.dt)}<br>
             속도 ${esc(e.speed ?? "-")} km/h<br>
             처리 ${esc(RESULT_LABEL[e.result] ?? e.result)}
             <div style="margin-top:5px;padding-top:5px;border-top:1px solid #e5e7eb">
               차량 <b>${esc(e.vhcl_nm || "-")}</b>${tn ? ` (${esc(tn)})` : ""}<br>
               단말 ${esc(e.dvc_sn || "-")}<br>
               소속 ${esc(e.ogdp_nm || "-")}
             </div>`;
        if (onOpenRawRef.current) {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = "원시 데이터 보기 (그래프·표·엑셀)";
          btn.style.cssText =
            "display:block;width:100%;margin-top:6px;padding:5px 0;font-size:12px;border:1px solid #25863F;" +
            "border-radius:4px;background:#25863F;color:#fff;cursor:pointer";
          btn.addEventListener("click", () => onOpenRawRef.current?.(e.id));
          box.appendChild(btn);
        }
        infowRef.current.setContent(box);
        infowRef.current.open(map, marker);
      });
      return marker;
    });
    if (cl) cl.addMarkers(markers);
    else markers.forEach((mk: any) => mk.setMap(map));
    return () => {
      if (!cl) markers.forEach((mk: any) => mk.setMap(null));
    };
  }, [map, mode, shownEvents]);

  // ── 행정동 단계구분도 ──
  const breaks = useMemo(() => {
    if (!regions) return [];
    return quantileBreaks(regions.regions.map((r) => metricValue(r, metric)));
  }, [regions, metric]);

  useEffect(() => {
    const kakao = getKakao();
    for (const p of polysRef.current) p.setMap(null);
    polysRef.current = [];
    setPicked(null);
    if (!map || !kakao || mode !== "region" || !regions) return;
    for (const r of regions.regions) {
      const ring = regions.polygons[r.adm_cd];
      if (!ring || ring.length < 3) continue;
      const v = metricValue(r, metric);
      const lowSample = metric === "rate" && r.hours < RATE_MIN_HOURS;
      const color = lowSample || v <= 0 ? "#d1d5db" : CHORO_COLORS[classOf(v, breaks)];
      const poly = new kakao.maps.Polygon({
        map,
        path: ring.map(([la, ln]) => new kakao.maps.LatLng(la, ln)),
        strokeWeight: 1,
        strokeColor: "#ffffff",
        strokeOpacity: 0.9,
        fillColor: color,
        fillOpacity: lowSample || v <= 0 ? 0.35 : 0.7,
      });
      kakao.maps.event.addListener(poly, "mouseover", () => poly.setOptions({ strokeWeight: 3, strokeColor: "#111827" }));
      kakao.maps.event.addListener(poly, "mouseout", () => poly.setOptions({ strokeWeight: 1, strokeColor: "#ffffff" }));
      kakao.maps.event.addListener(poly, "click", () => setPicked(r));
      polysRef.current.push(poly);
    }
  }, [map, mode, regions, metric, breaks]);

  const metricMeta = REGION_METRICS.find((m) => m.key === metric)!;

  return (
    <Wrap style={{ height }}>
      <div ref={elRef} className="map" />
      {map && mode === "density" && (
        <HeatLayer map={map} points={shownEvents.map((e) => ({ lat: e.lat, lng: e.lot }))} radius={20} saturateAt={4} />
      )}

      {failed && (
        <div className="cover">
          지도를 불러오지 못했다. 카카오 개발자 콘솔에 이 주소({window.location.origin})가 등록돼 있는지 확인한다.
        </div>
      )}
      {loading && <div className="badge">{loading}</div>}

      {mode !== "region" && (
        <div className="legend">
          {EVENT_GROUPS.filter((g) => groups.has(g.key)).map((g) => (
            <span key={g.key}>
              <i style={{ background: g.color }} />
              {g.label}
            </span>
          ))}
          <b>{fmtInt(shownEvents.length)}건</b>
        </div>
      )}

      {mode === "region" && regions && (
        <div className="legend col">
          <div className="title">{metricMeta.label}</div>
          {breaks.length === 0 && <div>표시할 값이 없다</div>}
          {[0, ...breaks].map((b, i) => (
            <div key={i}>
              <i style={{ background: CHORO_COLORS[i] }} />
              {fmt1(b)}
              {i < breaks.length ? ` ~ ${fmt1(breaks[i])}` : " 이상"}
            </div>
          ))}
          <div>
            <i style={{ background: "#d1d5db" }} />
            {metric === "rate" ? `0 또는 운행 ${RATE_MIN_HOURS}시간 미만` : "0"}
          </div>
          <div className="note">
            행정동 {fmtInt(regions.regions.length)}곳 · 경계 밖 운행 {fmtInt(regions.unmatchedTrips)}건
          </div>
        </div>
      )}

      {picked && (
        <div className="panel">
          <button type="button" className="x" aria-label="닫기" onClick={() => setPicked(null)}>
            &times;
          </button>
          <div className="nm">{picked.adm_nm}</div>
          <table>
            <tbody>
              <tr>
                <th>운행</th>
                <td>
                  {fmtInt(picked.trips)}건 · {fmt1(picked.hours)}시간
                </td>
              </tr>
              <tr>
                <th>전복</th>
                <td>{fmtInt(picked.rollover)}건</td>
              </tr>
              <tr>
                <th>위험운전</th>
                <td>{fmtInt(picked.danger)}건</td>
              </tr>
              <tr>
                <th>이탈·위험지역</th>
                <td>{fmtInt(picked.area)}건</td>
              </tr>
              <tr>
                <th>1만 시간당 전복</th>
                <td>
                  {picked.hours >= RATE_MIN_HOURS
                    ? fmt1(per10kHours(picked.rollover, picked.hours))
                    : `- (운행 ${RATE_MIN_HOURS}시간 미만)`}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  background: #eef0ec;

  .map {
    position: absolute;
    inset: 0;
  }
  .cover {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: grid;
    place-content: center;
    padding: 24px;
    text-align: center;
    color: #6b7280;
    font-size: 13px;
    background: #eef0ec;
  }
  .badge {
    position: absolute;
    z-index: 6;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(17, 24, 39, 0.82);
    color: #fff;
    font-size: 12px;
    padding: 5px 12px;
    border-radius: 14px;
  }
  .legend {
    position: absolute;
    z-index: 4;
    left: 10px;
    bottom: 10px;
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    max-width: calc(100% - 20px);
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: #374151;
  }
  .legend.col {
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;
  }
  .legend .title {
    font-weight: 600;
    margin-bottom: 2px;
  }
  .legend .note {
    color: #6b7280;
    font-size: 11px;
    margin-top: 2px;
  }
  .legend i {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    margin-right: 5px;
    vertical-align: -1px;
  }
  .panel {
    position: absolute;
    z-index: 6;
    top: 10px;
    left: 10px;
    width: 240px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.16);
    padding: 12px 14px;
    font-size: 12.5px;
  }
  .panel .nm {
    font-weight: 600;
    font-size: 13px;
    margin: 0 18px 8px 0;
  }
  .panel th {
    text-align: left;
    color: #6b7280;
    font-weight: 400;
    padding: 2px 8px 2px 0;
    white-space: nowrap;
  }
  .panel td {
    padding: 2px 0;
  }
  .panel .x {
    position: absolute;
    top: 6px;
    right: 8px;
    border: 0;
    background: transparent;
    font-size: 17px;
    cursor: pointer;
    color: #6b7280;
  }
`;
